import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Drawer,
  IconButton,
  InputBase,
  Typography,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
  useMediaQuery,
  Skeleton,
  alpha,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckIcon from '@mui/icons-material/Check';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { toggleSidebar } from '../../../redux/actions/chatUIAction';
import {
  createConversation,
  fetchConversations,
  updateConversationTitle,
  deleteConversations,
} from '../../../redux/actions/conversationAction';
import { clearDocument } from '../../../redux/actions/documentAction';
import TutorialModal from '../../../components/TutorialModal';
import TutorialPinpoint from '../../../components/TutorialPinpoint';

/**
 * ConversationSidebar - Left sidebar with conversation history
 * Features:
 * - Collapsible sidebar (280px → 70px)
 * - New conversation button
 * - Conversation list with active highlighting
 * - Mobile drawer support
 */
const ConversationSidebar = ({ mobileDrawerOpen: mobileDrawerOpenProp, onMobileDrawerClose }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { conversationId } = useParams();

  // Media queries
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg')); // >= 1200px

  // Redux state
  const conversationState = useSelector(state => state.conversations || { conversations: [], loading: false });
  const { conversations, loading: conversationsLoading } = conversationState;
  const { sidebarCollapsed } = useSelector(state => state.chatUI || { sidebarCollapsed: false });
  const authState = useSelector(state => state.auth || {});
  const userId = authState.user?.user?.id;

  // Local state - use props if provided (mobile controlled from parent), else local state
  const [mobileDrawerOpenLocal, setMobileDrawerOpenLocal] = useState(false);
  const mobileDrawerOpen = mobileDrawerOpenProp !== undefined ? mobileDrawerOpenProp : mobileDrawerOpenLocal;
  const setMobileDrawerOpen = onMobileDrawerClose
    ? (open) => { if (!open) onMobileDrawerClose(); }
    : setMobileDrawerOpenLocal;
  const [creatingConversation, setCreatingConversation] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const CONVERSATIONS_PER_PAGE = 10;

  // Inline title editing state
  const [editingConvId, setEditingConvId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const editInputRef = useRef(null);

  useEffect(() => {
    if (editingConvId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingConvId]);

  const handleStartEdit = (e, conv) => {
    e.stopPropagation();
    setEditingConvId(conv.id);
    setEditingTitle(conv.title || '');
  };

  const handleSaveEdit = (e) => {
    if (e) e.stopPropagation();
    const title = editingTitle.trim();
    if (title && editingConvId) {
      dispatch(updateConversationTitle(editingConvId, title));
    }
    setEditingConvId(null);
    setEditingTitle('');
  };

  const handleCancelEdit = () => {
    setEditingConvId(null);
    setEditingTitle('');
  };

  const handleEditKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') handleSaveEdit();
    if (e.key === 'Escape') handleCancelEdit();
  };

  // Tutorial video modal
  const [chatVideoModalOpen, setChatVideoModalOpen] = useState(false);

  // Delete conversation state
  const [deleteConfirmConv, setDeleteConfirmConv] = useState(null);

  const handleDeleteClick = (e, conv) => {
    e.stopPropagation();
    setDeleteConfirmConv(conv);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmConv) return;
    const deletedId = deleteConfirmConv.id;
    setDeleteConfirmConv(null);
    await dispatch(deleteConversations([deletedId], userId));
    dispatch(fetchConversations(userId, CONVERSATIONS_PER_PAGE, 0, { silent: true }));
    // If deleting the active conversation, navigate to a new one
    if (deletedId === conversationId) {
      handleNewConversation();
    }
  };

  // Fetch conversations on mount
  useEffect(() => {
    if (userId) {
      dispatch(fetchConversations(userId, CONVERSATIONS_PER_PAGE, 0));
      setOffset(0);
      setHasMore(true);
    }
  }, [dispatch, userId]);

  // Handle load more conversations
  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      const newOffset = offset + CONVERSATIONS_PER_PAGE;
      const moreConversations = await dispatch(
        fetchConversations(userId, CONVERSATIONS_PER_PAGE, newOffset)
      );

      // Check if we got fewer conversations than requested (indicates end of list)
      if (moreConversations && moreConversations.length < CONVERSATIONS_PER_PAGE) {
        setHasMore(false);
      }

      setOffset(newOffset);
    } catch (error) {
      console.error('Error loading more conversations:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Handle new conversation
  const handleNewConversation = async () => {
    setCreatingConversation(true);
    dispatch(clearDocument());
    try {
      // Refetch conversations silently to update titles of previous conversations
      await dispatch(fetchConversations(userId, CONVERSATIONS_PER_PAGE, 0, { silent: true }));
      setOffset(0);
      setHasMore(true);

      const newConversation = await dispatch(createConversation(userId));
      if (newConversation?.id) {
        navigate(`/conversation/${newConversation.id}`);
        if (isMobile) {
          setMobileDrawerOpen(false);
        }
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
    } finally {
      setCreatingConversation(false);
    }
  };

  // Handle conversation selection
  const handleSelectConversation = (convId) => {
    navigate(`/conversation/${convId}`);
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  // Toggle sidebar collapse
  const handleToggleSidebar = () => {
    dispatch(toggleSidebar());
  };

  // Mobile drawer content
  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: theme.palette.background.navbar,
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Chat
            </Typography>
            <TutorialPinpoint storageKey="tutorial_pinpoint_chat">
              <IconButton
                size="small"
                onClick={() => setChatVideoModalOpen(true)}
                title="Tutorial video"
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.18) },
                }}
              >
                <PlayCircleOutlineIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </TutorialPinpoint>
          </Box>
          {isMobile && (
            <IconButton size="small" onClick={() => setMobileDrawerOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* New Conversation Button */}
      <Box sx={{ px: 2, pb: 2 }}>
        <Button
          variant="contained"
          fullWidth
          startIcon={<AddIcon />}
          onClick={handleNewConversation}
          disabled={creatingConversation}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          {creatingConversation ? 'Creating...' : 'New Chat'}
        </Button>
      </Box>

      <Divider />

      {/* Conversations List */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Typography
          variant="caption"
          sx={{
            px: 2,
            py: 1,
            display: 'block',
            fontWeight: 600,
            textTransform: 'uppercase',
            color: theme.palette.text.secondary,
            letterSpacing: '0.06em',
            flexShrink: 0,
          }}
        >
          {t('contractTabs.recent', 'Recent')}
        </Typography>

        <List sx={{ py: 0, flex: 1, overflowY: 'auto' }}>
          {conversationsLoading ? (
            Array.from({ length: 5 }).map((_, idx) => (
              <ListItem key={idx} sx={{ px: 1 }}>
                <Skeleton variant="text" width="100%" height={40} />
              </ListItem>
            ))
          ) : conversations && conversations.length > 0 ? (
            conversations.map(conv => (
              <ListItemButton
                key={conv.id}
                selected={conv.id === conversationId}
                onClick={() => editingConvId !== conv.id && handleSelectConversation(conv.id)}
                sx={{
                  mx: 1,
                  my: 0.5,
                  borderRadius: '8px',
                  pr: 0.5,
                  color: conv.id === conversationId ? theme.palette.primary.main : theme.palette.text.primary,
                  backgroundColor:
                    conv.id === conversationId ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                  '&:hover': {
                    backgroundColor:
                      conv.id === conversationId
                        ? alpha(theme.palette.primary.main, 0.1)
                        : alpha(theme.palette.primary.main, 0.04),
                    '& .edit-btn': { opacity: 1 },
                  },
                  opacity: conv.id === conversationId ? 1 : 0.8,
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                {editingConvId === conv.id ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 0.5 }}>
                    <InputBase
                      inputRef={editInputRef}
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      onKeyDown={handleEditKeyDown}
                      onBlur={handleSaveEdit}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        flex: 1,
                        fontSize: '14px',
                        fontWeight: 500,
                        color: theme.palette.text.primary,
                        '& input': { p: 0 },
                      }}
                    />
                    <IconButton
                      size="small"
                      onMouseDown={(e) => { e.preventDefault(); handleSaveEdit(e); }}
                      sx={{ p: 0.25, color: theme.palette.primary.main }}
                    >
                      <CheckIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
                    <ListItemText
                      sx={{ flex: 1, minWidth: 0, mr: 0 }}
                      primary={
                        <Typography noWrap sx={{ fontSize: '14px', fontWeight: conv.id === conversationId ? 600 : 500 }}>
                          {conv.title || 'Untitled Conversation'}
                        </Typography>
                      }
                      secondary={
                        conv.updated_at ? (
                          <Typography component="span" variant="caption" noWrap sx={{ color: theme.palette.text.secondary, fontSize: '12px' }}>
                            {new Date(conv.updated_at).toLocaleDateString()}
                          </Typography>
                        ) : null
                      }
                    />
                    <IconButton
                      className="edit-btn"
                      size="small"
                      onClick={(e) => handleStartEdit(e, conv)}
                      sx={{ opacity: 0, p: 0.5, flexShrink: 0, color: theme.palette.text.secondary, transition: 'opacity 0.15s', '&:hover': { color: theme.palette.primary.main } }}
                    >
                      <EditIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                    <IconButton
                      className="edit-btn"
                      size="small"
                      onClick={(e) => handleDeleteClick(e, conv)}
                      sx={{ opacity: 0, p: 0.5, flexShrink: 0, color: theme.palette.text.secondary, transition: 'opacity 0.15s', '&:hover': { color: theme.palette.error.main } }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </Box>
                )}
              </ListItemButton>
            ))
          ) : (
            <ListItem>
              <ListItemText
                primary={
                  <Typography variant="body2" color="text.secondary">
                    No conversations yet
                  </Typography>
                }
              />
            </ListItem>
          )}
        </List>

        {/* View More Button */}
        {conversations && conversations.length >= CONVERSATIONS_PER_PAGE && hasMore && (
          <Box sx={{ px: 2, pb: 2, borderTop: `1px solid ${theme.palette.divider}`, flexShrink: 0 }}>
            <Button
              fullWidth
              size="small"
              onClick={handleLoadMore}
              disabled={loadingMore}
              sx={{
                textTransform: 'none',
                fontSize: '12px',
                color: 'primary.main',
              }}
            >
              {loadingMore ? 'Loading...' : 'View More'}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );

  // Desktop sidebar
  const desktopSidebar = (
    <Box
      sx={{
        width: sidebarCollapsed ? 70 : 280,
        flexShrink: 0,
        backgroundColor: theme.palette.background.navbar,
        borderRight: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Header with toggle */}
      <Box
        sx={{
          p: sidebarCollapsed ? 1 : 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: sidebarCollapsed ? 'center' : 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {!sidebarCollapsed && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Chat
            </Typography>
            <TutorialPinpoint storageKey="tutorial_pinpoint_chat">
              <IconButton
                size="small"
                onClick={() => setChatVideoModalOpen(true)}
                title="Tutorial video"
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: theme.palette.primary.main,
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.18) },
                }}
              >
                <PlayCircleOutlineIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </TutorialPinpoint>
          </Box>
        )}
        <IconButton
          size="small"
          onClick={handleToggleSidebar}
          sx={{ p: 1 }}
          title={sidebarCollapsed ? 'Expand' : 'Collapse'}
        >
          {sidebarCollapsed ? (
            <ChevronRightIcon fontSize="small" />
          ) : (
            <ChevronLeftIcon fontSize="small" />
          )}
        </IconButton>
      </Box>

      {/* New Conversation Button */}
      <Box
        sx={{
          p: sidebarCollapsed ? 1 : 2,
          pb: 1,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        {sidebarCollapsed ? (
          <IconButton
            onClick={handleNewConversation}
            disabled={creatingConversation}
            title="New conversation"
            sx={{
              color: 'primary.main',
              p: 1.5,
            }}
          >
            <AddIcon />
          </IconButton>
        ) : (
          <Button
            variant="contained"
            fullWidth
            startIcon={<AddIcon />}
            onClick={handleNewConversation}
            disabled={creatingConversation}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            {creatingConversation ? 'Creating...' : 'New Chat'}
          </Button>
        )}
      </Box>

      {!sidebarCollapsed && <Divider />}

      {/* Conversations List */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {!sidebarCollapsed && (
          <Typography
            variant="caption"
            sx={{
              px: 2,
              py: 1,
              display: 'block',
              fontWeight: 600,
              textTransform: 'uppercase',
              color: theme.palette.text.secondary,
              letterSpacing: '0.06em',
              flexShrink: 0,
            }}
          >
            {t('contractTabs.recent', 'Recent')}
          </Typography>
        )}

        <List sx={{ py: 0, flex: 1, overflowY: 'auto' }}>
          {conversationsLoading ? (
            // Skeleton loaders
            sidebarCollapsed ? (
              <ListItem sx={{ px: 1, justifyContent: 'center' }}>
                <Skeleton variant="circular" width={40} height={40} />
              </ListItem>
            ) : (
              Array.from({ length: 5 }).map((_, idx) => (
                <ListItem key={idx} sx={{ px: 1 }}>
                  <Skeleton variant="text" width="100%" height={40} />
                </ListItem>
              ))
            )
          ) : conversations && conversations.length > 0 ? (
            (sidebarCollapsed ? conversations.slice(0, 1) : conversations).map(conv => (
              <ListItemButton
                key={conv.id}
                selected={conv.id === conversationId}
                onClick={() => editingConvId !== conv.id && handleSelectConversation(conv.id)}
                title={sidebarCollapsed ? (conv.title || 'Untitled Conversation') : undefined}
                sx={{
                  mx: sidebarCollapsed ? 0.5 : 1,
                  my: 0.5,
                  borderRadius: '8px',
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                  px: sidebarCollapsed ? 1 : 1,
                  py: sidebarCollapsed ? 1.5 : 1,
                  pr: sidebarCollapsed ? 1 : 0.5,
                  color: conv.id === conversationId ? theme.palette.primary.main : theme.palette.text.primary,
                  backgroundColor:
                    conv.id === conversationId
                      ? alpha(theme.palette.primary.main, 0.1)
                      : 'transparent',
                  '&:hover': {
                    backgroundColor:
                      conv.id === conversationId
                        ? alpha(theme.palette.primary.main, 0.1)
                        : alpha(theme.palette.primary.main, 0.04),
                    '& .edit-btn': { opacity: 1 },
                  },
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                {sidebarCollapsed ? (
                  <ChatBubbleOutlineIcon sx={{ fontSize: 20 }} />
                ) : editingConvId === conv.id ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 0.5 }}>
                    <InputBase
                      inputRef={editInputRef}
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      onKeyDown={handleEditKeyDown}
                      onBlur={handleSaveEdit}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        flex: 1,
                        fontSize: '14px',
                        fontWeight: 500,
                        color: theme.palette.text.primary,
                        '& input': { p: 0 },
                      }}
                    />
                    <IconButton
                      size="small"
                      onMouseDown={(e) => { e.preventDefault(); handleSaveEdit(e); }}
                      sx={{ p: 0.25, color: theme.palette.primary.main }}
                    >
                      <CheckIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
                    <ListItemText
                      sx={{ flex: 1, minWidth: 0, mr: 0 }}
                      primary={
                        <Typography noWrap sx={{ fontSize: '14px', fontWeight: 500 }}>
                          {conv.title || 'Untitled Conversation'}
                        </Typography>
                      }
                      secondary={
                        conv.updated_at ? (
                          <Typography component="span" variant="caption" noWrap sx={{ color: theme.palette.text.secondary, fontSize: '12px' }}>
                            {new Date(conv.updated_at).toLocaleDateString()}
                          </Typography>
                        ) : null
                      }
                    />
                    <IconButton
                      className="edit-btn"
                      size="small"
                      onClick={(e) => handleStartEdit(e, conv)}
                      sx={{ opacity: 0, p: 0.5, flexShrink: 0, color: theme.palette.text.secondary, transition: 'opacity 0.15s', '&:hover': { color: theme.palette.primary.main } }}
                    >
                      <EditIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                    <IconButton
                      className="edit-btn"
                      size="small"
                      onClick={(e) => handleDeleteClick(e, conv)}
                      sx={{ opacity: 0, p: 0.5, flexShrink: 0, color: theme.palette.text.secondary, transition: 'opacity 0.15s', '&:hover': { color: theme.palette.error.main } }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </Box>
                )}
              </ListItemButton>
            ))
          ) : null}
        </List>

        {/* View More Button - Desktop */}
        {!sidebarCollapsed &&
          conversations &&
          conversations.length >= CONVERSATIONS_PER_PAGE &&
          hasMore && (
            <Box
              sx={{
                px: 2,
                pb: 2,
                borderTop: `1px solid ${theme.palette.divider}`,
                flexShrink: 0,
              }}
            >
              <Button
                fullWidth
                size="small"
                onClick={handleLoadMore}
                disabled={loadingMore}
                sx={{
                  textTransform: 'none',
                  fontSize: '12px',
                  color: 'primary.main',
                }}
              >
                {loadingMore ? 'Loading...' : 'View More'}
              </Button>
            </Box>
          )}
      </Box>
    </Box>
  );

  // Delete confirmation dialog
  const deleteDialog = (
    <Dialog
      open={!!deleteConfirmConv}
      onClose={() => setDeleteConfirmConv(null)}
      maxWidth="xs"
    >
      <DialogTitle>Supprimer la conversation</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Êtes-vous sûr de vouloir supprimer cette conversation ? Cette action est irréversible et toutes les données associées seront supprimées.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeleteConfirmConv(null)}>Annuler</Button>
        <Button onClick={handleConfirmDelete} color="error" variant="contained">
          Supprimer
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Mobile drawer
  if (isMobile) {
    return (
      <>
        {/* Drawer - controlled from ConversationLayout header */}
        <Drawer
          anchor="left"
          open={mobileDrawerOpen}
          onClose={() => setMobileDrawerOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              width: 280,
            },
          }}
        >
          {drawerContent}
        </Drawer>
        {deleteDialog}
        <TutorialModal
          open={chatVideoModalOpen}
          onClose={() => setChatVideoModalOpen(false)}
          defaultTab="chat"
        />
      </>
    );
  }

  // Desktop sidebar
  return (
    <>
      {desktopSidebar}
      {deleteDialog}
      <TutorialModal
        open={chatVideoModalOpen}
        onClose={() => setChatVideoModalOpen(false)}
        defaultTab="chat"
      />
    </>
  );
};

export default ConversationSidebar;
